/**
 * Which order this browser tab is holding for a payment attempt, so only that tab releases the hold.
 *
 * `release-payment` can't tell one attempt from another: a second tab releasing on Back or on reload
 * would end the first tab's live hold — mid-Klarna redirect, say — and reopen the race the hold
 * closes. `sessionStorage` is per tab and survives a payment provider's redirect round trip in that
 * tab, which is exactly the scope needed. Read and written in `try`, since storage can be blocked.
 * Rationale: `docs/features/2026.10.01-deferred-intent-payment-hold/implementation-plans/2026.10.01-001-deferred-intent-payment-hold.md`
 *
 * A copy in `localStorage` tells the browser's other tabs that one of them is paying, so a tab that
 * finds the cart paid waits for the paying tab to finish rather than clearing the cart it still needs.
 */
const KEY = 'paymentHoldOrderId'
const BROWSER_KEY = 'paymentInProgress'

/** How long the browser-wide copy is believed: the hold's own expiry (`StripeSettings.PaymentHoldMinutes`). */
const BROWSER_PAYMENT_MAX_AGE_MS = 30 * 60_000

interface BrowserPayment {
  orderId: string
  startedAt: number
}

export function markPaymentHold(orderId: string): void {
  try {
    sessionStorage.setItem(KEY, orderId)
    localStorage.setItem(
      BROWSER_KEY,
      JSON.stringify({ orderId, startedAt: Date.now() } satisfies BrowserPayment)
    )
  } catch {
    // Without the marker this tab won't release early; the hold's expiry still does.
  }
}

export function clearPaymentHold(): void {
  try {
    const orderId = sessionStorage.getItem(KEY)
    sessionStorage.removeItem(KEY)
    // Another tab may have started a payment of its own since.
    if (orderId && readBrowserPayment()?.orderId === orderId) localStorage.removeItem(BROWSER_KEY)
  } catch {
    // Nothing to clear when storage is unavailable.
  }
}

/** Whether this tab took the payment hold on `orderId`. */
export function tabHoldsPayment(orderId: string | null | undefined): boolean {
  if (!orderId) return false

  try {
    return sessionStorage.getItem(KEY) === orderId
  } catch {
    return false
  }
}

/** Whether a tab in this browser is paying for `orderId`, this one or another. */
export function browserPaymentInProgress(orderId: string | null | undefined): boolean {
  if (!orderId) return false

  try {
    const payment = readBrowserPayment()
    return (
      payment?.orderId === orderId && Date.now() - payment.startedAt < BROWSER_PAYMENT_MAX_AGE_MS
    )
  } catch {
    return false
  }
}

function readBrowserPayment(): BrowserPayment | null {
  const stored = localStorage.getItem(BROWSER_KEY)
  if (!stored) return null

  try {
    const payment = JSON.parse(stored) as Partial<BrowserPayment> | null
    return typeof payment?.orderId === 'string' && typeof payment.startedAt === 'number'
      ? (payment as BrowserPayment)
      : null
  } catch {
    return null
  }
}
